import React, { useState } from "react";
import { Modal, Popconfirm, Tag, Tooltip, Typography } from "antd";
import { InfoCircleOutlined, CaretRightOutlined } from "@ant-design/icons";
import { FlagConfig, FetchParam } from "@store/reducers/userDefinedFlags.reducer";
import { SCOPE_OPTIONS, STAT_OPTIONS } from "./constants";

const { Text } = Typography;

const SCOPE_LABELS: Record<string, string> = Object.fromEntries(SCOPE_OPTIONS.map((o) => [o.value, o.label]));
const STAT_LABELS: Record<string, string> = Object.fromEntries(STAT_OPTIONS.map((o) => [o.value, o.label]));
const VARIABLE_LABELS: Record<string, string> = {
    latest: "Current date value",
    max: "All Time Maximum",
    rolling_avg_7d: "7-Day Rolling Avg",
    rolling_avg_30d: "30-Day Rolling Avg",
    rolling_std_dev_30d: "30-Day Rolling Std Dev",
};

interface Operand {
    name: string;
    metric: string;
    scope: string;
    agg: string;
    variable: string;
}

interface Example {
    condition: string;
    meaning: string;
    operands: Operand[];
}

const EXAMPLES: Example[] = [
    {
        condition: `"Total Distance" > 5000`,
        meaning: "Today's distance goes above 5000",
        operands: [
            { name: "Total Distance", metric: "Distance", scope: "current:player", agg: "sum", variable: "latest" },
        ],
    },
    {
        condition: `"7D Avg Hours of Sleep" < 6.5`,
        meaning: "Today's 7-day sleep average is under 6.5 hours",
        operands: [
            { name: "7D Avg Hours of Sleep", metric: "Hours of Sleep", scope: "current:player", agg: "avg", variable: "rolling_avg_7d" },
        ],
    },
    {
        condition: `"Player Top Speed" > 0.9 * "ATH Top Speed"`,
        meaning: "Today's top speed reaches at least 90% of their all-time best",
        operands: [
            { name: "Player Top Speed", metric: "Top Speed", scope: "current:player", agg: "best", variable: "latest" },
            { name: "ATH Top Speed", metric: "Top Speed", scope: "current:player", agg: "best", variable: "max" },
        ],
    },
    {
        condition: `("Pre-Train Wt" - "Post-Train Wt") / "Pre-Train Wt" > 0.05`,
        meaning: "Today's session weight loss is more than 5% of body weight",
        operands: [
            { name: "Pre-Train Wt", metric: "Pre-Training Weight", scope: "current:player", agg: "avg", variable: "latest" },
            { name: "Post-Train Wt", metric: "Post-Training Weight", scope: "current:player", agg: "avg", variable: "latest" },
        ],
    },
    {
        condition: `"Player Load" > "30D Avg" + (2 * "30D SD")`,
        meaning: "Today's player load is over 2 standard deviations above the 30-day average",
        operands: [
            { name: "Player Load", metric: "Player Load", scope: "current:player", agg: "sum", variable: "latest" },
            { name: "30D Avg", metric: "Player Load", scope: "current:player", agg: "sum", variable: "rolling_avg_30d" },
            { name: "30D SD", metric: "Player Load", scope: "current:player", agg: "sum", variable: "rolling_std_dev_30d" },
        ],
    },
];

// Turn an example (quoted names) into a builder FlagConfig: each unique operand becomes opN,
// scope/variable are carried over, but metric_identifier is left blank for the user to pick.
const exampleToConfig = (ex: Example): FlagConfig => {
    let condition = ex.condition;
    const fetchParams: Record<string, FetchParam> = {};
    ex.operands.forEach((op, idx) => {
        const opName = `op${idx + 1}`;
        condition = condition.split(`"${op.name}"`).join(opName);
        fetchParams[opName] = { scope: op.scope, variable: op.variable, metric_identifier: "", label: op.name };
    });
    return { condition, fetch_params: fetchParams };
};

interface ConditionHelpModalProps {
    onApply: (config: FlagConfig) => void;
}

const ConditionHelpModal: React.FC<ConditionHelpModalProps> = ({ onApply }) => {
    const [open, setOpen] = useState(false);
    const [expanded, setExpanded] = useState<Set<number>>(new Set());

    const toggle = (i: number) =>
        setExpanded((prev) => {
            const next = new Set(prev);
            next.has(i) ? next.delete(i) : next.add(i);
            return next;
        });

    const applyExample = (ex: Example) => {
        onApply(exampleToConfig(ex));
        setOpen(false);
    };

    return (
        <>
            <Tooltip title="How to create a rule">
                <InfoCircleOutlined
                    className="udf-condition-help-icon"
                    onClick={() => setOpen(true)}
                />
            </Tooltip>
            <Modal
                title="How to create a rule"
                open={open}
                onCancel={() => setOpen(false)}
                footer={null}
                width={1000}
            >
                <div className="udf-condition-help">
                    <p>A rule is a check. When it's true for a player on a given day, this flag is raised.</p>

                    <ol>
                        <li>
                            <b>Name your values</b> in quotes, like <Text code>"Total Distance"</Text>. The name is
                            just a quick way to identify the value. You choose what it actually measures (the metric,
                            scope, and variable) in the block that appears below for each one.
                        </li>
                        <li>
                            <b>Compare</b> against a number or another value with{" "}
                            <Text code>&gt;</Text> <Text code>&lt;</Text> <Text code>&gt;=</Text>{" "}
                            <Text code>&lt;=</Text> <Text code>=</Text>.
                        </li>
                        <li>
                            <b>Do math</b> (optional) with <Text code>+</Text> <Text code>-</Text>{" "}
                            <Text code>*</Text> <Text code>/</Text> and <Text code>( )</Text>.
                        </li>
                    </ol>

                    <table className="udf-condition-help-examples">
                        <thead>
                            <tr>
                                <th>Rule</th>
                                <th>What it flags</th>
                            </tr>
                        </thead>
                        <tbody>
                            {EXAMPLES.map((ex, i) => {
                                const isOpen = expanded.has(i);
                                return (
                                    <React.Fragment key={ex.condition}>
                                        <tr className="udf-example-row" onClick={() => toggle(i)}>
                                            <td>
                                                <div className="udf-example-cond-cell">
                                                    <span className="udf-example-cond">
                                                        <CaretRightOutlined
                                                            className="udf-example-caret"
                                                            rotate={isOpen ? 90 : 0}
                                                        />
                                                        <code>{ex.condition}</code>
                                                    </span>
                                                    <Popconfirm
                                                        title="Use this rule?"
                                                        description={
                                                            <span>
                                                                This replaces your current rule. Since it's just
                                                                an example, you'll need to pick a matching metric from
                                                                your list for each value. If one isn't on your account,
                                                                choose an alternative.
                                                            </span>
                                                        }
                                                        okText="Yes, use it"
                                                        cancelText="Cancel"
                                                        overlayStyle={{ maxWidth: 320 }}
                                                        onConfirm={() => applyExample(ex)}
                                                    >
                                                        <Tag
                                                            color="blue"
                                                            className="udf-example-use"
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            Use this rule
                                                        </Tag>
                                                    </Popconfirm>
                                                </div>
                                            </td>
                                            <td>{ex.meaning}</td>
                                        </tr>
                                        {isOpen && (
                                            <tr className="udf-example-detail">
                                                <td colSpan={2}>
                                                    {ex.operands.map((op) => (
                                                        <div key={op.name} className="udf-operand-detail">
                                                            <div className="udf-operand-name">{op.name}</div>
                                                            <div className="udf-operand-config">Metric = {op.metric}</div>
                                                            <div className="udf-operand-config">Scope = {SCOPE_LABELS[op.scope] ?? op.scope}</div>
                                                            <div className="udf-operand-config">Agg = {STAT_LABELS[op.agg] ?? op.agg}</div>
                                                            <div className="udf-operand-config">Variable = {VARIABLE_LABELS[op.variable] ?? op.variable}</div>
                                                        </div>
                                                    ))}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </Modal>
        </>
    );
};

export default ConditionHelpModal;
